import { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { StyledTableCell, StyledTableRow } from "../../../../components/StyledTableComponents";
import Page from "../../../../components/Page";
import { TDiscount } from "../types";
import { ORDER_TYPE_LABELS } from "../../schemas";

const PAGE_SIZE = 10;

type DiscountListProps = {
  discounts: TDiscount[];
  onEdit: (discount: TDiscount) => void;
  onDelete: (discount: TDiscount) => void;
  onToggleStatus: (discount: TDiscount) => void;
};

const getDiscountValue = (discount: TDiscount) => {
  if (discount.tiers && discount.tiers.length > 0) {
    return `${discount.tiers.length} tier(s)`;
  }
  if (discount.discountType === "percentage") {
    return `${discount.discountPercentage}%`;
  }
  if (discount.discountAmount) {
    return `${discount.discountAmount.currency} ${discount.discountAmount.value}`;
  }
  return "-";
};

const getStatusColor = (status?: string) => {
  if (status === "active") return { bg: "#e8f5e9", color: "#2e7d32" };
  if (status === "expired") return { bg: "#fff3e0", color: "#e65100" };
  return { bg: "#ffebee", color: "#c62828" };
};

const DiscountList = ({ discounts, onEdit, onDelete, onToggleStatus }: DiscountListProps) => {
  const [pageNum, setPageNum] = useState(1);

  if (!discounts || discounts.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography sx={styles.emptyText}>No discounts found</Typography>
      </Box>
    );
  }

  const totalPages = Math.ceil(discounts.length / PAGE_SIZE);
  const paginatedDiscounts = discounts.slice(
    (pageNum - 1) * PAGE_SIZE,
    pageNum * PAGE_SIZE
  );

  return (
    <>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Value</StyledTableCell>
              <StyledTableCell>Order Types</StyledTableCell>
              <StyledTableCell>Min Purchase</StyledTableCell>
              <StyledTableCell>Usage</StyledTableCell>
              <StyledTableCell>Validity</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {paginatedDiscounts.map((discount) => {
              const statusStyle = getStatusColor(discount.status);
              return (
                <StyledTableRow key={discount._id}>
                  <StyledTableCell sx={styles.descriptionCell}>
                    {discount.description}
                  </StyledTableCell>
                  <StyledTableCell sx={styles.capitalize}>
                    {discount.discountType}
                  </StyledTableCell>
                  <StyledTableCell>{getDiscountValue(discount)}</StyledTableCell>
                  <StyledTableCell>
                    {discount.applicableOrderType.length > 0
                      ? discount.applicableOrderType
                          .map((t) => ORDER_TYPE_LABELS[t])
                          .filter(Boolean)
                          .join(", ")
                      : "All"}
                  </StyledTableCell>
                  <StyledTableCell>
                    {discount.minPurchase
                      ? `${discount.minPurchase.currency} ${discount.minPurchase.value}`
                      : "-"}
                  </StyledTableCell>
                  <StyledTableCell>
                    {discount.usageCount != null
                      ? `${discount.usageCount}${discount.usageLimit ? ` / ${discount.usageLimit}` : ""}`
                      : "-"}
                  </StyledTableCell>
                  <StyledTableCell sx={styles.validityCell}>
                    {new Date(discount.validFrom).toLocaleDateString()} -{" "}
                    {new Date(discount.validTo).toLocaleDateString()}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={discount.status?.toUpperCase() || "ACTIVE"}
                      size="small"
                      sx={{
                        backgroundColor: statusStyle.bg,
                        color: statusStyle.color,
                        fontFamily: "GilroySemiBold",
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Box sx={styles.actionBox}>
                      <Tooltip title={discount.status === "active" ? "Disable" : "Enable"}>
                        <Switch
                          size="small"
                          checked={discount.status === "active"}
                          onChange={() => onToggleStatus(discount)}
                          sx={styles.statusSwitch}
                        />
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(discount)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Disable">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onDelete(discount)}
                            disabled={discount.status === "disabled"}
                            sx={{
                              color: "#f44336",
                              ...(discount.status === "disabled" && { opacity: 0.3 }),
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Grid container mt={3} justifyContent="center">
          <Page
            totalPages={totalPages}
            pageNum={pageNum}
            onSelectPage={(value: number) => setPageNum(value)}
          />
        </Grid>
      )}
    </>
  );
};

export default DiscountList;

const styles = {
  tableContainer: {
    boxShadow: "none",
    borderRadius: "8px",
    overflow: "hidden",
  },
  emptyContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "3rem",
    borderRadius: "8px",
    height: "6rem",
  },
  emptyText: {
    color: "#999",
    fontSize: "14px",
    fontFamily: "InterRegular",
  },
  descriptionCell: {
    maxWidth: 220,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  capitalize: {
    textTransform: "capitalize" as const,
  },
  validityCell: {
    whiteSpace: "nowrap",
  },
  actionBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "4px",
  },
  statusSwitch: {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#4caf50",
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#4caf50",
    },
  },
};
